import { CaretLeft } from "@phosphor-icons/react";
import { Fragment, type ReactNode } from "react";
import { Link, type LinkProps, NavLink, useLocation } from "react-router-dom";

import NavItem from "./NavItem";

interface SidebarItem {
  icon: ReactNode;
  label: string;
  path: string;
  subNavItems?: Pick<SidebarItem, "path" | "label">[];
}

type DesktopSidebarNavigationProps = {
  title: string;
  navItems: (SidebarItem | { element: ReactNode; key?: string })[];
  backLinkProps: LinkProps;
  footer?: ReactNode;
};

export default function DesktopSidebarNavigation({
  title,
  navItems,
  backLinkProps,
  footer,
}: DesktopSidebarNavigationProps) {
  const { pathname } = useLocation();

  return (
    <div className="flex flex-col bg-white/50 shadow-xl rounded-3xl w-52 shrink-0 max-lg:hidden h-full">
      <div className="px-4 py-3.5 flex">
        <Link
          {...backLinkProps}
          className="flex items-center gap-x-2.5 p-1 text-black-950 hover:text-black-600 transition-[color]"
        >
          <CaretLeft size={16} />
          <h2 className="text-md-bold">{title}</h2>
        </Link>
      </div>

      <div className="flex flex-col py-4 px-3 scrollbar-thin overflow-y-auto gap-y-1">
        <div className="flex flex-col gap-y-1">
          {navItems.map((item, index) => {
            if ("element" in item) {
              return (
                <Fragment key={item.key ?? `sidebar-element-${index}`}>
                  {item.element}
                </Fragment>
              );
            }
            const { icon, path, subNavItems, label } = item;
            const activeSubNavIndex = subNavItems?.findIndex(
              ({ path: subNavPath }) =>
                pathname.endsWith(subNavPath ? `${path}/${subNavPath}` : path),
            );

            return (
              <Fragment key={label}>
                {subNavItems ? (
                  <NavItem isExpanded isInteractive={false} icon={icon}>
                    {label}
                  </NavItem>
                ) : (
                  <NavLink to={path} key={label}>
                    {({ isActive }) => (
                      <NavItem isExpanded isActive={isActive} icon={icon}>
                        {label}
                      </NavItem>
                    )}
                  </NavLink>
                )}

                {subNavItems && (
                  <div className="flex">
                    <div className="px-3 py-1">
                      <div className="w-px bg-black-200 h-full relative flex flex-col items-center">
                        {activeSubNavIndex !== -1 && (
                          <div
                            className="rounded-full w-0.5 h-7 bg-purple-600 absolute transition-[top]"
                            style={{ top: `${activeSubNavIndex * 40}px` }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-y-1 grow">
                      {subNavItems.map(
                        ({ path: subNavPath, label: subNavLabel }) => (
                          <NavLink
                            to={subNavPath ? `${path}/${subNavPath}` : path}
                            key={subNavPath}
                            end
                          >
                            {({ isActive }) => (
                              <NavItem
                                isExpanded
                                isActive={isActive}
                                icon={null}
                              >
                                {subNavLabel}
                              </NavItem>
                            )}
                          </NavLink>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className="mt-auto w-full px-2">{footer}</div>
    </div>
  );
}
